import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { ActionItemSummaryDto, GetPlanDto, queryKeys, useGetPlan, useGetPlanSubitems } from "src/http";
import { IPlan } from "src/models";

const useGetPlanTree = (planGuid: string, dateTimeLocal?: string, dateTimeLocalUtcOffsetInMinutes?: number) => {
  const { data, isLoading } = useGetPlan(planGuid, dateTimeLocal, dateTimeLocalUtcOffsetInMinutes);
  const { data: subitemsData, fetchNextPage } = useGetPlanSubitems(planGuid);
  const [plan, setPlan] = useState<IPlan>();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data !== undefined) {
      setPlan(data);
    }
  }, [data, subitemsData]);

  async function fetchActionItemSubitems(actionItemGuid: string) {
    const cachedPlan = queryClient.getQueryData<GetPlanDto>(queryKeys.plans.getPlan(planGuid));

    if (cachedPlan === undefined) {
      return;
    }

    let subitems: ActionItemSummaryDto[] = [];

    // If subitems have already been cached, get them from the cache.
    if (subitemsData?.pageParams.some((pageParam) => pageParam === actionItemGuid)) {
      subitems = getActionItemSubitemsFromCache(actionItemGuid);
    } else {
      let fetchResult = await fetchNextPage({ pageParam: actionItemGuid });

      if (fetchResult.data?.pageParams.some((pageParam) => pageParam === actionItemGuid)) {
        let pageIndex = fetchResult.data.pageParams.indexOf(actionItemGuid);
        subitems = fetchResult.data.pages[pageIndex];
      }
    }

    const updatedCachedPlan = produce(cachedPlan, (draft) => {
      let actionItems = draft.blocks.flatMap((block) => block.sections.flatMap((section) => section.actionItems)) ?? [];
      let matchingActionItems = searchActionItems(actionItems, (actionItem) => actionItem.guid === actionItemGuid);

      matchingActionItems?.forEach((actionItem) => {
        actionItem.subitems = subitems;
      });
    });

    queryClient.setQueryData(queryKeys.plans.getPlan(planGuid), updatedCachedPlan);
  }

  function getActionItemSubitemsFromCache(actionItemGuid: string) {
    if (subitemsData === undefined) {
      return [];
    }

    let pageIndex = subitemsData.pageParams.indexOf(actionItemGuid);
    if (pageIndex === -1) {
      return [];
    }

    let subitems = subitemsData.pages[pageIndex];

    // Check to see if the subitem has any of its subitems already cached.
    let subitemsWithSubitems = produce(subitems, (draft) => {
      draft.forEach((subitem) => {
        subitem.subitems = getActionItemSubitemsFromCache(subitem.guid);
      });
    });

    return subitemsWithSubitems;
  }

  return {
    plan,
    setPlan,
    isLoading,
    fetchActionItemSubitems,
  };
};

const searchActionItems = (
  actionItems: ActionItemSummaryDto[],
  predicate: (actionItem: ActionItemSummaryDto) => boolean
): ActionItemSummaryDto[] | undefined => {
  var results: ActionItemSummaryDto[] = [];

  for (let i = 0; i < actionItems.length; i++) {
    var item = actionItems[i];

    if (predicate(item)) {
      results.push(item);
    }

    if (item.subitems.length > 0) {
      var subitemResults = searchActionItems(item.subitems, predicate);

      if (subitemResults) {
        results.push(...subitemResults);
      }
    }
  }

  if (results.length > 0) {
    return results;
  } else {
    return undefined;
  }
};

export { useGetPlanTree };
